import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiTrash2, FiEye, FiSearch, FiPhone, FiMapPin, FiBriefcase, FiUser, FiCheck } from 'react-icons/fi';
import { UseToast } from '../../hooks/UseToast';
import Badge from '../../components/ui/Badge';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const MessagesList = () => {
  const navigate = useNavigate();
  const toast = UseToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/service-messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();

      if (data.success) {
        setMessages(data.data || []);
      } else {
        toast.error('Failed to load messages');
        setMessages([]);
      }
    } catch (error) {
      toast.error('Server error');
      console.error('Fetch error:', error);
      setMessages([]);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/service-messages/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await res.json();

      if (data.success) {
        toast.success('Message deleted successfully');
        setMessages(messages.filter(m => m._id !== id));
      } else {
        toast.error(data.message || 'Delete failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Delete error:', error);
    }
  };

  const filteredMessages = messages.filter(msg => {
    const matchesSearch =
      msg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.message?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || msg.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const statusMap = {
      new: { color: '#F59E0B', bg: '#FEF3C7', label: 'New' },
      contacted: { color: '#3B82F6', bg: '#DBEAFE', label: 'Contacted' },
      closed: { color: '#10B981', bg: '#D1FAE5', label: 'Closed' }
    };
    return statusMap[status] || statusMap.new;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <style>{`
.messages-page { font-family: 'Outfit', sans-serif; }
.page-header { margin-bottom: 30px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 20px; }
.page-title { font-size: 32px; font-weight: 800; color: #0F172A; margin: 0; }
.page-subtitle { color: #64748B; font-size: 15px; margin: 0; }
.filters-bar { background: #FFF; padding: 20px; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); margin-bottom: 24px; display: flex; gap: 16px; align-items: center; }
.search-input { flex: 1; display: flex; align-items: center; gap: 10px; background: #F1F5F9; padding: 12px 16px; border-radius: 10px; }
.search-input input { border: none; background: none; outline: none; flex: 1; font-size: 14px; font-family: inherit; }
.filter-select { padding: 12px 16px; border: 2px solid #E2E8F0; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; background: #FFF; font-family: inherit; }
.messages-list { background: #FFF; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); overflow: hidden; }
.message-item { padding: 20px 24px; border-bottom: 1px solid #F1F5F9; display: flex; align-items: center; gap: 16px; transition: all 0.3s ease; cursor: pointer; }
.message-item:hover { background: #F8FAFC; }
.message-item.new { background: #EFF6FF; border-left: 4px solid #3B82F6; }
.message-icon { width: 48px; height: 48px; border-radius: 12px; background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%); display: flex; align-items: center; justify-content: center; color: #FFF; font-size: 20px; flex-shrink: 0; }
.message-content { flex: 1; }
.message-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; flex-wrap: wrap; gap: 8px; }
.message-name { font-size: 16px; font-weight: 700; color: #0F172A; }
.message-date { font-size: 13px; color: #64748B; }
.message-service { font-size: 14px; font-weight: 600; color: #3B82F6; margin-bottom: 4px; }
.message-preview { font-size: 13px; color: #64748B; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.message-actions { display: flex; gap: 8px; flex-shrink: 0; }
.action-btn { width: 36px; height: 36px; border-radius: 8px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.3s ease; font-size: 16px; }
.action-btn-view { background: #DBEAFE; color: #3B82F6; }
.action-btn-view:hover { background: #3B82F6; color: #FFF; }
.action-btn-delete { background: #FEE2E2; color: #EF4444; }
.action-btn-delete:hover { background: #EF4444; color: #FFF; }
.loading-state,.empty-state { text-align: center; padding: 60px 20px; color: #64748B; }
.empty-icon { font-size: 64px; margin-bottom: 16px; opacity: 0.5; }
        @media (max-width: 768px) {
     .filters-bar { flex-direction: column; }
     .search-input { width: 100%; }
        }
      `}</style>
      <div className="messages-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Service Messages</h1>
            <p className="page-subtitle">Manage service enquiries from customers</p>
          </div>
          <Badge color="#3B82F6" bg="#DBEAFE">
            {messages.filter(m => m.status === 'new').length} New
          </Badge>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <FiSearch />
            <input
              type="text"
              placeholder="Search by name, email, service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Messages</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="messages-list">
          {loading ? (
            <div className="loading-state">Loading messages...</div>
          ) : filteredMessages.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📬</div>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A' }}>No messages found</p>
              <p style={{ fontSize: '14px', color: '#64748B' }}>New enquiries will appear here</p>
            </div>
          ) : (
            filteredMessages.map((msg) => {
              const badge = getStatusBadge(msg.status);
              return (
                <div key={msg._id} className={`message-item ${msg.status}`} onClick={() => navigate(`/messages/${msg._id}`)}>
                  <div className="message-icon"><FiMail /></div>
                  <div className="message-content">
                    <div className="message-header">
                      <div>
                        <span className="message-name">{msg.name}</span>
                        <span style={{ marginLeft: '12px' }}>
                          <Badge color={badge.color} bg={badge.bg}>{badge.label}</Badge>
                        </span>
                      </div>
                      <span className="message-date">{formatDate(msg.createdAt)}</span>
                    </div>
                    <div className="message-service">{msg.service} • {msg.business}</div>
                    <div className="message-preview">{msg.message}</div>
                  </div>
                  <div className="message-actions">
                    <button
                      className="action-btn action-btn-view"
                      onClick={(e) => { e.stopPropagation(); navigate(`/messages/${msg._id}`); }}
                      title="View Details"
                    >
                      <FiEye />
                    </button>
                    <button
                      className="action-btn action-btn-delete"
                      onClick={(e) => { e.stopPropagation(); handleDelete(msg._id); }}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default MessagesList;